import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { storageService } from '../services/storageService';
import FileUpload from '../components/FileUpload';
import SEO from '../components/SEO';
import { generateUniqueRaceSlug, generateCategorySlug } from '../utils/slugify';

const AdminDashboard: React.FC = () => {
    const { isAdmin, currentUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [migrationStatus, setMigrationStatus] = useState<string>('');
    const [migrationLoading, setMigrationLoading] = useState(false);

    const [isLinking, setIsLinking] = useState(false);
    const [linkStatus, setLinkStatus] = useState<string>('');

    // Admin File Upload State
    const [targetUserId, setTargetUserId] = useState('');
    const [adminUploadStatus, setAdminUploadStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    useEffect(() => {
        // Redirect if not admin (Client-side check)
        if (currentUser && !isAdmin) {
            navigate('/');
            return;
        }
        
        if (!currentUser) return;
        
        // Temporarily clear loading immediately since beta requests logic was removed
        setLoading(false);
    }, [currentUser, isAdmin, navigate]);

    const runMigrationFunction = async (functionName: string) => {
        if (!confirm(`Are you sure you want to run ${functionName}?`)) return;
        
        setMigrationLoading(true);
        setMigrationStatus(`Triggering ${functionName}... check browser console for detailed output.`);
        
        try {
            const functions = getFunctions();
            const callable = httpsCallable(functions, functionName);
            const result = await callable();
            
            console.log(`=== ${functionName} Result ===`, result.data);
            setMigrationStatus(`✅ ${functionName} completed successfully! Check console for data.`);
        } catch (error: any) {
            console.error(`${functionName} failed:`, error);
            setMigrationStatus(`❌ Error: ${error.message}`);
        } finally {
            setMigrationLoading(false);
        }
    };

    // Wipe BikeReg CRON entries directly from the frontend
    const handleNukeBikeReg = async () => {
        if (!confirm("Are you sure you want to wipe ALL BikeReg races? This cannot be undone.")) return;
        
        setMigrationLoading(true);
        setMigrationStatus('Nuking BikeReg races... please wait.');
        
        try {
            const db = firebase.firestore();
            const snapshot = await db.collection('races').where('externalSource', '==', 'BikeReg').get();
            
            if (snapshot.empty) {
                setMigrationStatus("✅ No BikeReg races found.");
                setMigrationLoading(false);
                return;
            }

            let batch = db.batch();
            let opCount = 0;
            let deletedCount = 0;

            for (const doc of snapshot.docs) {
                // 1. Delete the category subdocuments
                const catsSnap = await doc.ref.collection('categories').get();
                for (const catDoc of catsSnap.docs) {
                    batch.delete(catDoc.ref);
                    opCount++;
                    if (opCount >= 400) { await batch.commit(); batch = db.batch(); opCount = 0; }
                }
                // 2. Delete the parent document
                batch.delete(doc.ref);
                opCount++;
                deletedCount++;
                if (opCount >= 400) { await batch.commit(); batch = db.batch(); opCount = 0; }
            }
            if (opCount > 0) await batch.commit();
            
            setMigrationStatus(`✅ Successfully deleted ${deletedCount} BikeReg races.`);
        } catch (error: any) {
            console.error(error);
            setMigrationStatus(`❌ Error deleting races: ${error.message}`);
        } finally {
            setMigrationLoading(false);
        }
    };

    const handleRunAutoLinker = async () => {
        if (!confirm("Are you sure you want to force a sync with Bikedata.club?")) return;
        
        setIsLinking(true);
        setLinkStatus('Syncing with Bikedata.club... please wait.');
        try {
            const functions = getFunctions();
            const autoLinkFn = httpsCallable(functions, 'autoLinkExternalRaces');
            const result = await autoLinkFn();
            console.log("=== autoLinkExternalRaces Result ===", result.data);
            setLinkStatus(`✅ Sync complete! Check browser console for detailed payload.`);
        } catch (error: any) {
            console.error("Linking failed", error);
            setLinkStatus(`❌ Sync failed: ${error.message}`);
        } finally {
            setIsLinking(false);
        }
    };

    const handleAdminFileUpload = async (file: File) => {
        if (!targetUserId.trim()) {
            setAdminUploadStatus({ message: 'Please enter a target User ID first.', type: 'error' });
            throw new Error('Missing User ID');
        }
        
        try {
            await storageService.uploadPendingRaceFile(file, targetUserId.trim(), setAdminUploadStatus);
        } catch (error) {
            console.error('Admin file upload failed:', error);
            setAdminUploadStatus({ message: error instanceof Error ? error.message : 'Upload failed. Please try again.', type: 'error' });
            throw error;
        }
    };

    const handleGenerateSlugs = async () => {
        if (!confirm("Are you sure you want to generate slugs for all existing races?")) return;
        
        setMigrationLoading(true);
        setMigrationStatus('Generating slugs for races and categories... please wait.');
        
        try {
            const firestoreDb = firebase.firestore();
            const snapshot = await firestoreDb.collection('races').get();
            
            if (snapshot.empty) {
                setMigrationStatus("✅ No races found.");
                setMigrationLoading(false);
                return;
            }

            let batch = firestoreDb.batch();
            let opCount = 0;
            let updatedCount = 0;

            for (const doc of snapshot.docs) {
                const raceData = doc.data();
                if (!raceData.slug && raceData.name) {
                    const newSlug = await generateUniqueRaceSlug(raceData.name);
                    batch.update(doc.ref, { slug: newSlug });
                    opCount++;
                }

                const catsSnap = await doc.ref.collection('categories').get();
                for (const catDoc of catsSnap.docs) {
                    const catData = catDoc.data();
                    if (!catData.slug && catData.name) {
                        const newCatSlug = generateCategorySlug(catData.name);
                        batch.update(catDoc.ref, { slug: newCatSlug });
                        opCount++;
                    }
                    if (opCount >= 400) { await batch.commit(); batch = firestoreDb.batch(); opCount = 0; }
                }
                
                updatedCount++;
                if (opCount >= 400) { await batch.commit(); batch = firestoreDb.batch(); opCount = 0; }
            }
            if (opCount > 0) await batch.commit();
            
            setMigrationStatus(`✅ Successfully generated slugs for ${updatedCount} races.`);
        } catch (error: any) {
            console.error(error);
            setMigrationStatus(`❌ Error generating slugs: ${error.message}`);
        } finally {
            setMigrationLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">Loading...</div>;
    if (!isAdmin) return null;

    return (
        <div className="min-h-screen bg-gray-900 text-white p-8 overflow-y-auto">
            <SEO title="Admin Dashboard | RaceLineIQ" />
            <div className="max-w-5xl mx-auto">
                <h1 className="text-3xl font-bold text-cyan-400 mb-2">Admin Dashboard</h1>
                <p className="text-gray-400 mb-8">Manage the platform and pending beta requests.</p>

                {/* MIGRATION TOOLS SECTION */}
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-8 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">Database Migration Tools</h2>
                    <p className="text-gray-400 mb-4 text-sm">
                        Run these functions in order to migrate the flat race structure to the new hierarchy. 
                        <strong> Open your browser's Developer Console (F12) to see the detailed result payloads!</strong>
                    </p>
                    
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => runMigrationFunction('migrateRaceHierarchy')}
                            disabled={migrationLoading}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                        >
                            1. Run Migration
                        </button>
                        <button
                            onClick={() => runMigrationFunction('verifyRaceHierarchyMigration')}
                            disabled={migrationLoading}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                        >
                            2. Run Verification
                        </button>
                        <button
                            onClick={() => runMigrationFunction('cleanupOldRaceDocs')}
                            disabled={migrationLoading}
                            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                        >
                            3. Cleanup Old Docs
                        </button>
                        <button
                            onClick={handleNukeBikeReg}
                            disabled={migrationLoading}
                            className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50 ml-auto"
                        >
                            ⚠️ Nuke BikeReg Races
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-4">
                        <button
                            onClick={handleGenerateSlugs}
                            disabled={migrationLoading}
                            className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                        >
                            Generate Slugs for All Races
                        </button>
                    </div>

                    {migrationStatus && (
                        <div className="mt-4 p-4 bg-gray-900 border border-gray-600 rounded-lg text-cyan-300 text-sm font-mono break-all">
                            {migrationStatus}
                        </div>
                    )}
                </div>

                {/* EXTERNAL INTEGRATIONS SECTION */}
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-8 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">External Integrations</h2>
                    <p className="text-gray-400 mb-4 text-sm">
                        Manually trigger the Bikedata.club synchronization to link official results to RacelineIQ events. 
                        This normally runs automatically every 12 hours.
                    </p>
                    
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={handleRunAutoLinker}
                            disabled={isLinking}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                        >
                            {isLinking ? 'Syncing...' : 'Force Sync External Races'}
                        </button>
                    </div>
                    {linkStatus && (
                        <div className="mt-4 p-4 bg-gray-900 border border-gray-600 rounded-lg text-cyan-300 text-sm font-mono break-all">
                            {linkStatus}
                        </div>
                    )}
                </div>

                {/* ADMIN FILE UPLOAD SECTION */}
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-8 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">Manual File Upload (Impersonate User)</h2>
                    <p className="text-gray-400 mb-4 text-sm">
                        Upload a `.fit`, `.gpx`, or `.tcx` file directly to a user's `pending_uploads` folder. The backend auto-matcher will process the file, assign it to the correct race, and link it to the provided User ID exactly as if they uploaded it themselves.
                    </p>
                    
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-1">Target User ID (e.g. Mike's User ID)</label>
                        <input
                            type="text"
                            value={targetUserId}
                            onChange={(e) => setTargetUserId(e.target.value)}
                            placeholder="Enter Firebase UID..."
                            className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        />
                    </div>

                    {adminUploadStatus && (
                        <div className={`p-3 rounded-lg text-sm mb-4 ${adminUploadStatus.type === 'success' ? 'bg-green-900/30 text-green-400 border border-green-800/50' : adminUploadStatus.type === 'error' ? 'bg-red-900/30 text-red-400 border border-red-800/50' : 'bg-slate-700/50 text-slate-300 border border-slate-600/50'}`}>
                            {adminUploadStatus.message}
                        </div>
                    )}
                    
                    <div className="w-full max-w-md">
                        <FileUpload onFileUpload={handleAdminFileUpload} label="Upload File on Behalf of User" />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminDashboard;